/**
 * Search results + bet cards in Floodlight (v1.71): type and polish only.
 *
 * Every colour / font literal on the /results surface (sgpFlowStyles,
 * unifiedBetCardStyles, SgpFlowScreenView, SgpResultsRenderer,
 * UnifiedBetCardView, RelatedMarkets) is written
 * `uiVar('rs-<role>', <exact classic literal>)`. Classic never defines
 * --bo-rs-*, so the fallback wins and Classic is pixel-identical.
 *
 * v1.71 (Trev, Oct 19: "keep our current background and glassmorphic style",
 * "our purple and blue accents remain"): Floodlight no longer recolours
 * results. It defines NO colour, fill or line variable and no longer hides the
 * page glow, the glass panel, the card gradient or the SGP badge, so those
 * render exactly as in Classic. Floodlight contributes only:
 *   - type: Archivo for body text (rs-font-body), tabular numerals, caps +
 *     tracking on the data-fl-results "label" / "display" hooks;
 *   - polish: a gentle card lift on hover and a purple focus ring, both
 *     motion-free under prefers-reduced-motion.
 * Results is no longer a dark-only special case: there is one Floodlight look.
 */
import { varBlock, registerUiStyleCss, FL_ANY } from '../uiStyleCss';

const ARCHIVO = 'Archivo, "Helvetica Neue", Arial, sans-serif';
const EASE_OUT = 'cubic-bezier(0.16,1,0.3,1)';
/** The original results accent (indigo/violet), used only for the focus ring. */
const ACCENT = '#A78BFA';

/** --bo-rs-* values Floodlight defines: typography only. */
export const RESULTS_FL_VARS = {
    'rs-font-body': ARCHIVO,
};

/** Var-name prefixes Floodlight must never define on results (colour, fill, line). */
export const RESULTS_COLOUR_PREFIXES = ['rs-text-', 'rs-fill-', 'rs-line-'];

const S = FL_ANY;

/** Scoped type + polish rules (Floodlight only). No colour, background or border overrides. */
export const RESULTS_RULES = `
${S} [data-fl-results="panel"] div[dir="auto"]:not([style*="font-family"]) { font-family: ${ARCHIVO}; }
${S} [data-fl-results="panel"] div[dir="auto"] { font-variant-numeric: tabular-nums; }
${S} [data-fl-results="display"] { font-family: ${ARCHIVO} !important; font-weight: 800 !important; font-stretch: 112%; letter-spacing: -0.01em; }
${S} [data-fl-results="label"] { font-family: ${ARCHIVO} !important; font-weight: 700 !important; font-stretch: 108%; letter-spacing: 0.08em; text-transform: uppercase; }
${S} [data-fl-results="title"] { font-weight: 800 !important; font-stretch: 104%; letter-spacing: -0.005em; }
${S} [data-card="bet"] { font-variant-numeric: tabular-nums; transition: translate 360ms ${EASE_OUT}; }
@media (hover: hover) and (pointer: fine) {
  ${S} [data-card="bet"]:hover { translate: 0 -2px; }
}
${S} [data-card="bet"] :focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  ${S} [data-card="bet"] { transition: none !important; }
  ${S} [data-card="bet"]:hover { translate: none !important; }
}
`;

export const RESULTS_CSS = `${FL_ANY}{${varBlock(RESULTS_FL_VARS)}}` + RESULTS_RULES;

/** Register the results CSS once (web only; no-op elsewhere). */
export function ensureResultsUiStyle() {
    registerUiStyleCss('results', RESULTS_CSS);
}

/** data-fl-results hook for the scoped rules: `<View {...flDs('panel')}>`. */
export const flDs = (role) => ({ dataSet: { flResults: role } });
